// @ts-nocheck
import type { Language } from '../types_i18n';
import type { Question } from '../types';
import type { MultilingualQuestion } from '../types/multilingualQuestion';
import { priorityRulesMultilingual } from '../question_data/translations/priorityRulesTranslations';
import { riskAwarenessMultilingual } from '../question_data/translations/riskAwarenessTranslations';
import { pedestrianCrossingsMultilingual } from '../question_data/translations/pedestrianCrossingsTranslations';
import { constructionZonesMultilingual } from '../question_data/translations/constructionZonesTranslations';
import { weatherConditionsMultilingual } from '../question_data/translations/weatherConditionsTranslations';
import { motorwayRulesMultilingual } from '../question_data/translations/motorwayRulesTranslations';
import { alcoholDrugsMultilingual } from '../question_data/translations/alcoholDrugsTranslations';
import { emergencyProceduresMultilingual } from '../question_data/translations/emergencyProceduresTranslations';
import { environmentalZonesMultilingual } from '../question_data/translations/environmentalZonesTranslations';
import { technologySafetyMultilingual } from '../question_data/translations/technologySafetyTranslations';
import { fatigueRestMultilingual } from '../question_data/translations/fatigueRestTranslations';
import { mandatorySignsMultilingual } from '../question_data/translations/mandatorySignsTranslations';
import { prohibitorySignsMultilingual } from '../question_data/translations/prohibitorySignsTranslations';
import { vehicleCategoriesMultilingual } from '../question_data/translations/vehicleCategoriesTranslations';
import { signIdentificationMultilingual } from '../question_data/translations/signIdentificationTranslations';
import { roadMarkingsMultilingual } from '../question_data/translations/roadMarkingsTranslations';
import { nightVisibilityMultilingual } from '../question_data/translations/nightVisibilityTranslations';
import { incidentFirstAidMultilingual } from '../question_data/translations/incidentFirstAidTranslations';
import { vehicleHandlingMultilingual } from '../question_data/translations/vehicleHandlingTranslations';
import { roundaboutRulesMultilingual } from '../question_data/translations/roundaboutRulesTranslations';
import { trafficLightsSignalsMultilingual } from '../question_data/translations/trafficLightsSignalsTranslations';
import { specializedDrivingMultilingual } from '../question_data/translations/specializedDrivingTranslations';
import { adasSafetyMultilingual } from '../question_data/translations/adasSafetyTranslations';
import { tramInteractionsMultilingual } from '../question_data/translations/tramInteractionsTranslations';
import { bicycleInteractionsMultilingual } from '../question_data/translations/bicycleInteractionsTranslations';
import { vehicleDocumentationMultilingual } from '../question_data/translations/vehicleDocumentationTranslations';
import { laneChangingMultilingual } from '../question_data/translations/laneChangingTranslations';
import { overtakingMultilingual } from '../question_data/translations/overtakingTranslations';
import { speedLimitsMultilingual } from '../question_data/translations/speedLimitsTranslations';
import { roadInformationMultilingual } from '../question_data/translations/roadInformationTranslations';
import { priorityRules2Multilingual } from '../question_data/translations/priorityRules2Translations';
import { trafficLightsSignals2Multilingual } from '../question_data/translations/trafficLightsSignals2Translations';
import { realExamMultilingual } from '../question_data/realExamMultilingual';

// Map of test IDs to their multilingual questions (CBR 2026 only)
const multilingualQuestions: Record<string, MultilingualQuestion[]> = {
  // CBR 2026 TIER 1: CRITICAL
  // `traffic-signs` practice topic mixes multiple question sources in `questionLoader`
  // (mandatory + prohibitory + sign identification), so we must provide a combined
  // multilingual pool here to avoid falling back to English for missing question IDs.
  'traffic-signs': [...mandatorySignsMultilingual, ...prohibitorySignsMultilingual, ...signIdentificationMultilingual],
  'priority-rules': priorityRulesMultilingual,
  'risk-awareness': riskAwarenessMultilingual,
  'road-markings': roadMarkingsMultilingual,
  'motorway-rules': motorwayRulesMultilingual,
  'vehicle-handling': vehicleHandlingMultilingual,
  'tram-interactions': tramInteractionsMultilingual,
  'night-visibility': nightVisibilityMultilingual,

  // CBR 2026 TIER 2: IMPORTANT
  'pedestrian-crossings': pedestrianCrossingsMultilingual,
  'weather-conditions': weatherConditionsMultilingual,
  'construction-zones': constructionZonesMultilingual,
  'technology-safety': technologySafetyMultilingual,
  'fatigue-rest': fatigueRestMultilingual,
  'environmental-zones': environmentalZonesMultilingual,
  'incident-first-aid': incidentFirstAidMultilingual,
  'vehicle-categories': vehicleCategoriesMultilingual,
  'vehicle-documentation': vehicleDocumentationMultilingual,

  // CBR 2026 TIER 3: ENHANCEMENT
  'roundabout-rules': roundaboutRulesMultilingual,
  'traffic-lights-signals': trafficLightsSignalsMultilingual,
  'emergency-procedures': emergencyProceduresMultilingual,
  'alcohol-drugs': alcoholDrugsMultilingual,
  'specialized-driving': specializedDrivingMultilingual,

  // BONUS
  'adas-safety': adasSafetyMultilingual,

  // Hazard / Bicycle
  'hazard-perception': riskAwarenessMultilingual,
  'bicycle-interactions': bicycleInteractionsMultilingual,

  // Backward compat (old test IDs)
  'traffic-lights': signIdentificationMultilingual,
  'road-signs': signIdentificationMultilingual,
  'mandatory-signs': mandatorySignsMultilingual,
  'warning-signs': signIdentificationMultilingual,
  'prohibitory-signs': prohibitorySignsMultilingual,
  'prohibitory-signs-2': prohibitorySignsMultilingual,
  'road-information': roadInformationMultilingual,
  'lane-changing': laneChangingMultilingual,
  'overtaking': overtakingMultilingual,
  'speed-limits': speedLimitsMultilingual,
  'priority-rules-2': priorityRules2Multilingual,
  'traffic-lights-signals-2': trafficLightsSignals2Multilingual,

  // Exam-only pool (practice topics use the keys above). See realExamMultilingual.ts.
  'real-exam-pool': realExamMultilingual,
};

// Convert multilingual question to regular question for a specific language
export const translateQuestion = (
  question: Question | MultilingualQuestion,
  language: Language
): Question => {
  // Check if it's already a multilingual question
  if ('text' in question && typeof question.text === 'object') {
    const mq = question as MultilingualQuestion;
    // Translate subject if it's a known test name
    let translatedSubject = mq.subject;
    if (mq.subject) {
      // Map common subject names to test IDs for translation
      const subjectMap: Record<string, string> = {
        'Traffic Lights & Signals': 'traffic-lights-signals',
        'Priority Rules': 'priority-rules',
        'Hazard Perception': 'hazard-perception',
        'Speed & Safety': 'speed-safety',
        'Bicycle Interactions': 'bicycle-interactions',
        'Roundabout Rules': 'roundabout-rules',
        'Tram Interactions': 'tram-interactions',
        'Pedestrian Crossings': 'pedestrian-crossings',
        'Construction Zones': 'construction-zones',
        'Weather Conditions': 'weather-conditions',
        'Road Signs': 'road-signs',
        'Mandatory Signs': 'mandatory-signs',
        'Prohibitory Signs': 'prohibitory-signs',
        'Warning Signs': 'warning-signs',
        'Road Information': 'road-information',
        'Motorway Rules': 'motorway-rules',
        'Vehicle Knowledge': 'vehicle-knowledge',
        'Parking Rules': 'parking-rules',
        'Environmental Zones': 'environmental',
        'Technology & Safety': 'technology-safety',
        'Alcohol & Drugs': 'alcohol-drugs',
        'Fatigue & Rest': 'fatigue-rest',
        'Emergency Procedures': 'emergency-procedures',
      };
      const testId = subjectMap[mq.subject];
      if (testId) {
        // Use translation service to get translated name
        // This will be handled by the component using getTranslatedTestNameForId
        // For now, keep original subject - component will translate it
        translatedSubject = mq.subject;
      }
    }
    const translatedText = mq.text[language] || mq.text.en;
    return {
      id: mq.id,
      question: translatedText,
      text: translatedText,
      options: mq.options.map(opt => ({
        id: opt.id,
        text: opt.text[language] || opt.text.en
      })),
      answerId: mq.correctAnswerId,
      correctAnswerId: mq.correctAnswerId,
      explanation: mq.explanation[language] || mq.explanation.en,
      subject: translatedSubject, // Subject will be translated by component
      topic: mq.subject || 'General',
      imageUrl: mq.imageUrl,
      imageHint: mq.imageHint,
      animationId: mq.animationId,
      questionType: mq.questionType,
      yesNoMapping: mq.yesNoMapping,
      fillinAnswer: mq.fillinAnswer,
      fillinAccept: mq.fillinAccept,
      fillinUnit: mq.fillinUnit,
    };
  }

  // It's a regular question, return as-is (English only)
  return question as Question;
};

// Get translated questions for a test
export const getTranslatedQuestions = (
  testId: string,
  questions: Question[],
  language: Language
): Question[] => {
  // Check if we have multilingual versions for this test
  const multilingualVersions = multilingualQuestions[testId];
  
  if (multilingualVersions && language !== 'en') {
    // Create a map of multilingual questions by ID for fast lookup
    const multilingualMap = new Map<string, MultilingualQuestion>();
    multilingualVersions.forEach(q => {
      multilingualMap.set(q.id, q);
    });
    
    // Match base questions with multilingual versions by ID
    // This ensures we only translate questions that exist in the base set
    const translated = questions.map(question => {
      const multilingualVersion = multilingualMap.get(question.id);
      if (multilingualVersion) {
        const t = translateQuestion(multilingualVersion, language);
        // Preserve media / layout fields from English base when JSON omits them
        return {
          ...question,
          ...t,
          text: t.text,
          question: t.question ?? t.text,
          options: t.options,
          explanation: t.explanation,
          correctAnswerId: t.correctAnswerId,
          answerId: t.answerId,
          imageUrl: t.imageUrl ?? question.imageUrl,
          imageHint: t.imageHint ?? question.imageHint,
          animationId: t.animationId ?? question.animationId,
          questionType: t.questionType ?? question.questionType,
          yesNoMapping: t.yesNoMapping ?? question.yesNoMapping,
          fillinAnswer: t.fillinAnswer ?? question.fillinAnswer,
          fillinAccept: t.fillinAccept ?? question.fillinAccept,
          fillinUnit: t.fillinUnit ?? question.fillinUnit,
        };
      }
      // No translation found - return original question (English)
      return question;
    });
    
    return translated;
  }

  // Return original questions (English)
  return questions;
};

// Check if a test has translations available
export const hasTranslations = (testId: string): boolean => {
  return testId in multilingualQuestions;
};

// Get list of tests with translations
export const getTranslatedTestIds = (): string[] => {
  return Object.keys(multilingualQuestions);
};

// Subject display names for each test ID
const testIdToSubject: Record<string, string> = {
  'traffic-lights': 'Traffic Lights',
  'traffic-lights-signals': 'Traffic Lights', // Backward compat
  'priority-rules': 'Priority Rules',
  'hazard-perception': 'Hazard Perception',
  'speed-limits': 'Speed Limits',
  'speed-safety': 'Speed Limits', // Backward compat
  'bicycle-interactions': 'Bicycle Interactions',
  'roundabout-rules': 'Roundabout Rules',
  'parking-rules': 'Parking Rules',
  'pedestrian-crossings': 'Pedestrian Crossings',
  'tram-interactions': 'Tram Interactions',
  'construction-zones': 'Construction Zones',
  'weather-conditions': 'Weather Conditions',
  'motorway-rules': 'Motorway Rules',
  'alcohol-drugs': 'Alcohol & Drugs',
  'emergency-procedures': 'Emergency Procedures',
  'safety-rules': 'Safety Rules',
  'environmental': 'Environmental Zones',
  'technology-safety': 'Technology & Safety',
  'fatigue-rest': 'Fatigue & Rest',
  'mandatory-signs': 'Mandatory Signs',
  'warning-signs': 'Warning Signs',
  'prohibitory-signs': 'Prohibitory Signs',
  'prohibitory-signs-2': 'Prohibitory Signs',
  'vehicle-knowledge': 'Vehicle Knowledge',
  'road-information': 'Road Information',
  'road-markings': 'Road Markings',
  'traffic-signs': 'Traffic Signs',
  'road-signs': 'Traffic Signs', // Backward compat
  'insight-practice': 'Insight Practice',
  'risk-awareness': 'Risk Awareness',
  'night-visibility': 'Night Visibility',
  'incident-first-aid': 'Incident & First Aid',
  'vehicle-handling': 'Vehicle Handling',
  'lane-changing': 'Lane Changing',
  'overtaking': 'Overtaking',
};

// Build a flat map of all multilingual questions by ID (lazy, cached)
let _allMultilingualById: Map<string, MultilingualQuestion> | null = null;
/** Flat ID → multilingual question across all CBR pools (tools / coverage audits). */
export function getMultilingualRegistry(): Map<string, MultilingualQuestion> {
  return getAllMultilingualById();
}

function getAllMultilingualById(): Map<string, MultilingualQuestion> {
  if (!_allMultilingualById) {
    _allMultilingualById = new Map();
    Object.values(multilingualQuestions).forEach(mqs => {
      mqs.forEach(mq => _allMultilingualById!.set(mq.id, mq));
    });
  }
  return _allMultilingualById;
}

// Translate any array of questions by ID lookup across ALL multilingual pools.
// Questions whose IDs are not in any pool are returned unchanged (English).
// Used by AI Exam and as the second pass after getTranslatedQuestions so nl/ar users get Dutch/Arabic
// wherever a translation exists in any registered pool.
export const translateQuestionsById = (
  questions: Question[],
  language: Language
): Question[] => {
  if (language === 'en') return questions;
  const allById = getAllMultilingualById();
  return questions.map(q => {
    const mq = allById.get(q.id);
    if (!mq) return q;
    const t = translateQuestion(mq, language);
    return {
      ...q,
      ...t,
      text: t.text,
      question: t.question ?? t.text,
      options: t.options,
      explanation: t.explanation,
      correctAnswerId: t.correctAnswerId,
      answerId: t.answerId,
      imageUrl: t.imageUrl ?? q.imageUrl,
      imageHint: t.imageHint ?? q.imageHint,
      animationId: t.animationId ?? q.animationId,
      questionType: t.questionType ?? q.questionType,
      yesNoMapping: t.yesNoMapping ?? q.yesNoMapping,
      fillinAnswer: t.fillinAnswer ?? q.fillinAnswer,
      fillinAccept: t.fillinAccept ?? q.fillinAccept,
      fillinUnit: t.fillinUnit ?? q.fillinUnit,
    };
  });
};

// Return ALL non-image multilingual questions as plain Question[] (English text).
// Used as the regular-question pool for the AI exam so every question is translatable.
export const getAllRegularMultilingualQuestions = (): Question[] => {
  const result: Question[] = [];
  Object.entries(multilingualQuestions).forEach(([testId, mqs]) => {
    const subject = testIdToSubject[testId] || testId;
    mqs.forEach(mq => {
      if (mq.imageUrl) return; // skip image questions
      result.push({
        id: mq.id,
        text: mq.text.en,
        question: mq.text.en,
        options: mq.options.map(opt => ({ id: opt.id, text: opt.text.en })),
        correctAnswerId: mq.correctAnswerId,
        answerId: mq.correctAnswerId,
        explanation: mq.explanation.en,
        subject: mq.subject || subject,
        topic: mq.subject || subject,
        imageUrl: undefined,
        imageHint: mq.imageHint,
      } as Question);
    });
  });
  return result;
};
